import React from 'react'
import { Alert, Review } from 'hhsoig-design-system-core'
import {
  formatAddress,
  formatAttachedFiles,
  formatPersonName,
  replacePhraseWithString
} from '../../../../utilities'
import { dateToString } from '../../../../utilities/methods/dateToString'
import { Divider } from '../../../../components/divider'
import { PrintReview } from '../../../../components/PrintReview'
import { ReviewSubjects } from '../../../../components/ReviewSubjects'
import { ReviewWitnesses } from '../../../../components/ReviewWitnesses'
import css from './inspect.module.css'

export const InspectWBR = ({ goBackToPage, pages, values }) => {
  // Check the Anonymous options the complainant chose.
  if (
    values.anonymous_acknowledgement &&
    values.anonymous_acknowledgement[0] === 'Yes' &&
    values.perm_to_disclose
  ) {
    // If the complainant has twice chosen to be anonymous, remove the PID
    if (values.perm_to_disclose === 'Anonymous') {
      values.complainant_first_name = ''
      values.complainant_middle_name = ''
      values.complainant_last_name = ''
      values.complainant_suffix = ''
      values.complainant_personal_email = ''
      values.complainant_work_email = ''
      values.complainant_home_phone = ''
      values.complainant_cell_phone = ''
      values.complainant_work_phone = ''
      values.complainant_address.line1Value = ''
      values.complainant_address.line2Value = ''
      values.complainant_address.cityValue = ''
      values.complainant_address.stateValue = ''
      values.complainant_address.zipValue = ''
      values.complainant_address.countryValue = ''
      values.perm_to_disclose = ''
    } else {
      // If the complainant initially chose to be anonymous,
      // but then chose a non-anonymous option
      // remove the first anonymous choice.
      values.anonymous_acknowledgement = []
    }
  }

  const findPageIdOfField = name => {
    return pages.find(p => p.fields.some(f => f.name === name)).id
  }

  const retaliation_details = [
    {
      heading: 'What you experienced',
      field_name: 'retaliation_action',
      value: replacePhraseWithString(
        values.retaliation_action,
        values.retaliation_action_other
      ).join(', ')
    },
    {
      heading: 'Reason for retaliation',
      field_name: 'retaliation_reason',
      value: values.retaliation_reason
    }
  ]
  if (values.retaliation_reason === 'Reporting fraud, waste, or abuse') {
    retaliation_details.push(
      {
        heading: 'What you reported',
        field_name: 'disclosure_type',
        value: values.disclosure_type.join(', ')
      },
      {
        heading: 'Who you reported to',
        field_name: 'communication_contact',
        value: replacePhraseWithString(
          values.communication_contact,
          values.communication_contact_other
        ).join(', ')
      },
      {
        heading: 'Date of first report',
        field_name: 'disclosure_date',
        value: dateToString(values.disclosure_date, 'MDY')
      }
    )
  }
  retaliation_details.push({
    heading: 'Your work at the time',
    field_name: 'complainant_employer',
    value: replacePhraseWithString(
      values.complainant_employer,
      values.employer_other
    )
  })
  if (values.work_place) {
    retaliation_details.push({
      heading: 'Your work place',
      field_name: 'work_place',
      value: values.work_place
    })
  }
  if (values.work_address.line1Value) {
    retaliation_details.push({
      heading: 'Your work address',
      field_name: 'work_address',
      value: formatAddress(values.work_address)
    })
  }

  retaliation_details.push(
    {
      heading: 'Start date of the retaliation',
      field_name: 'date_of_activity',
      // value: moment(dateToString(values.date_of_activity)).format(dateFormat)
      value: dateToString(values.date_of_activity, 'MDY')
    },
    {
      heading: 'Is the retaliation still happening?',
      field_name: 'still_happening',
      value: values.still_happening
    }
  )
  if (values.still_happening === 'No') {
    retaliation_details.push({
      heading: 'End date of the retaliation',
      field_name: 'activity_end_date',
      // value: moment(dateToString(values.activity_end_date)).format(dateFormat)
      value: dateToString(values.activity_end_date, 'MDY')
    })
  }

  const complainant_details = []
  const complainant_alerts = []
  const formattedPersonName =
    values.complainant_first_name ||
    values.complainant_middle_name ||
    values.complainant_last_name
      ? formatPersonName(
          values.complainant_first_name,
          values.complainant_middle_name,
          values.complainant_last_name,
          values.complainant_suffix
        )
      : 'None provided'
  complainant_details.push({
    heading: 'Your name',
    field_name: 'complainant_first_name',
    value: formattedPersonName
  })
  if (values.complainant_home_phone) {
    complainant_details.push({
      heading: 'Your home phone',
      field_name: 'complainant_home_phone',
      value: values.complainant_home_phone
    })
  }
  if (values.complainant_cell_phone) {
    complainant_details.push({
      heading: 'Your cell phone',
      field_name: 'complainant_cell_phone',
      value: values.complainant_cell_phone
    })
  }
  if (values.complainant_work_phone) {
    complainant_details.push({
      heading: 'Your work phone',
      field_name: 'complainant_work_phone',
      value: values.complainant_work_phone
    })
  }
  if (values.complainant_personal_email) {
    complainant_details.push({
      heading: 'Your email address',
      field_name: 'complainant_personal_email',
      value: values.complainant_personal_email
    })
  }
  if (values.complainant_work_email) {
    complainant_details.push({
      heading: 'Your work email',
      field_name: 'complainant_work_email',
      value: values.complainant_work_email
    })
  }
  if (values.complainant_address.line1Value) {
    complainant_details.push({
      heading: 'Your home address',
      field_name: 'address',
      value: formatAddress(values.complainant_address)
    })
  }

  // Anonymous has a higher precidence, so check it first.
  if (values.perm_to_disclose) {
    complainant_details.push({
      heading: 'Consent to disclose your identity',
      field_name: 'perm_to_disclose',
      value: values.perm_to_disclose
    })
  } else if (values.non_anon_perm_to_disclose) {
    complainant_details.push({
      heading: 'Consent to disclose your identity',
      field_name: 'non_anon_perm_to_disclose',
      value: values.non_anon_perm_to_disclose
    })
  }

  if (
    values.anonymous_acknowledgement &&
    values.anonymous_acknowledgement[0] === 'Yes'
  ) {
    complainant_alerts.push({
      key: 61,
      heading: 'Acknowledgement',
      variation: 'warn',
      text: `I wish to remain anonymous. I understand this request will preclude me from having my whistleblower reprisal complaint investigated and may hinder HHS-OIG’s ability to thoroughly review and/or resolve any other allegations in my complaint. I also understand that the information I provide anonymously may be referred to another agency if HHS-OIG determines that action by another agency is warranted under the circumstances. There may be instances where for public safety, crime prevention, or other lawful purpose, HHS-OIG may need to trace your identity.`
    })
  }

  const description_details = [
    {
      heading: 'Your description of events',
      field_name: 'narrative',
      value: values.narrative
    },
    {
      heading: 'Attached files',
      field_name: 'documents',
      value: formatAttachedFiles(values.documents)
    }
  ]

  // Create and return a <Review> tag for each detail object in 'details'.
  // The detail objects are defined above, and have 4 elements:
  // key, heading, ediHref, value
  // key is used to prevent the React error about having a unique key for each item in an array.
  const iterateDetails = details => {
    return details.map(detail => (
      <Review
        className={css.reviewContent}
        key={detail.field_name}
        heading={detail.heading}
        onEditClick={() => {
          goBackToPage(findPageIdOfField(detail.field_name))
        }}
        editHref="javascript:void(0)"
      >
        {detail.value}
      </Review>
    ))
  }

  const iterateAlerts = alerts => {
    return (
      <div className="ds-u-margin-top--2">
        {alerts.map(alert => (
          <Alert
            heading={alert.heading}
            variation={alert.variation}
            key={alert.key}
          >
            {alert.text}
          </Alert>
        ))}
      </div>
    )
  }

  const hasAlerts = complainant_alerts.length > 0
  const onEditClick = field_name => goBackToPage(findPageIdOfField(field_name))

  return (
    <div data-testid="review-complaint">
      <h1 className="form-pg-title">Your complaint summary</h1>
      <p className="ds-text">
        Before you submit, make sure you check your information and save or
        print a copy for your records.
      </p>
      <h2>Retaliation details</h2>
      {iterateDetails(retaliation_details)}
      {values.subject_type_0 != 'unknown' && (
        <React.Fragment>
          <h2 className={css.reviewH2}>Subject information</h2>
          <ReviewSubjects values={values} onClick={onEditClick} />
        </React.Fragment>
      )}
      {values.witness_corroboration == 'Yes' && (
        <React.Fragment>
          <h2>Witness information</h2>
          <ReviewWitnesses values={values} onClick={onEditClick} />
        </React.Fragment>
      )}
      <h2 className={css.reviewH2}>Your details</h2>
      {iterateDetails(description_details)}
      <h2 className={css.reviewH2}>Your identity</h2>
      {iterateDetails(complainant_details)}
      {iterateAlerts(complainant_alerts)}
      {hasAlerts && (
        <div className={css.printReview}>
          <Divider />
        </div>
      )}
      <PrintReview className={css.printReview} />
    </div>
  )
}
